// lib/leasing/sourcingPreset.ts
//
// The user's reusable sourcing scoring preset: "save this scoring as my default", "apply my usual
// scoring here".
//
// A shortlist is scored on the one grid (lib/leasing/briefCriteria.ts) and read through its
// INSTRUCTIONS, the free prose mandate (a DoctrineDocument scoped CAMPAIGN, served by
// /api/doctrine?key=campaign:<campaignId>). A preset carries the instructions, parked on the user
// instead of on a shortlist; presets saved before the one grid also carry a rubric, which is read
// but no longer applied. It gets no table and no
// Prisma column of its own: it is a DoctrineDocument on the EXISTING user layer, scope USER, agent
// SOURCE, addressed by the key below.
//
// WHY THE RUBRIC IS SERIALIZED INTO THE PROSE, in a fenced ```json block behind a stable marker:
// a DoctrineDocument has exactly one payload column, `content` (String). It cannot carry a second
// typed field, and adding one would be the new column the brief rules out. Markdown with a fenced
// block is the only shape that keeps the document (a) a real, human-readable, hand-editable page in
// the doctrine workspace, and (b) a lossless carrier for the criteria: JSON.stringify /
// JSON.parse round-trip the array exactly, including `minimumScore` when it is set and its ABSENCE
// when it is not (the parser only re-emits the key for a finite number, so an unset floor never
// materialises as 0 on the way back). The HTML-comment marker is invisible when the markdown is
// rendered, so the machine block does not disfigure the page the user reads.

import type { KernelHandle, ScoringCriterion, SourcingPresetParams, SourcingPresetParamsSet } from '#types/types';

export type { SourcingPresetParams, SourcingPresetParamsSet };

/**
 * The preset's doctrine key. Same grammar as `org:<id>:tool:<TOOL>` and `team:<id>:tool:<TOOL>`,
 * minus the id segment: a personal layer's scopeId is ALWAYS the caller (parseSectionKey stamps
 * `actor.dbUserId` itself), which is what makes another user's preset unnameable, let alone
 * readable. See lib/server/doctrine.ts.
 */
export const SOURCING_PRESET_DOCTRINE_KEY = 'user:tool:SOURCE';

// The three stable anchors. All HTML comments, so they are invisible once the markdown is rendered
// and survive a user reflowing the prose around them. They are what makes parsing deterministic
// instead of heuristic: without the document marker, a preset saved with an EMPTY instructions half
// parses its own generated header back out as "instructions", and applying it would write our
// warning banner into the shortlist's doctrine.
/** Present at the very top of every document THIS module wrote. Absent = a hand-written page. */
const SOURCING_PRESET_DOCUMENT_MARKER = '<!-- fater:sourcing-preset -->';
/** Opens the prose half. */
const SOURCING_PRESET_INSTRUCTIONS_MARKER = '<!-- fater:sourcing-preset-instructions -->';
/** Opens the machine half: the fenced ```json rubric that follows it. */
export const SOURCING_PRESET_RUBRIC_MARKER = '<!-- fater:sourcing-preset-rubric -->';
/**
 * Opens the launch-parameters block. It sits BETWEEN the instructions and the rubric, never after
 * it: the rubric parser finds its block with `lastIndexOf` precisely because it is the last thing
 * in the document, and appending a fourth section below it would quietly break that.
 */
export const SOURCING_PRESET_PARAMS_MARKER = '<!-- fater:sourcing-preset-params -->';

/**
 * A stored criterion. `description` is not on ScoringCriterion but the rubric command writes one on
 * every criterion it saves, and it is what the Judge actually reads, so the preset carries it
 * through rather than silently flattening the rubric on the way out and back.
 */
export type SourcingPresetCriterion = ScoringCriterion & { description?: string };

/**
 * The launch parameters half: what the right panel's form holds before a run is launched.
 *
 * Deliberately a SUBSET of the panel, and deliberately typed off `PropertySourcingCategoryFilters`
 * rather than re-declared: a field added to the panel and forgotten here is a silent half-save, and
 * the way to make that impossible is to not own a second copy of the field list.
 *
 * What is NOT here, and must never be: the asset, the property, the portfolio. A preset is reusable
 * across assets by definition, and carrying a target id would silently retarget somebody's run -
 * the same class of bug as a draft campaign titled with the asset it happened to be opened from.
 */

export interface SourcingPreset {
  /** The prose half: the shortlist instructions, without the machine block or the header. */
  instructions: string;
  /** The points half. */
  rubric: SourcingPresetCriterion[];
  /**
   * The parameters half: NAMED sets, so a user can keep "ESQ params 1" beside "big-box setup".
   * UNDEFINED, not [], when the document has no params block - a preset saved before this existed
   * must leave the form alone rather than zero it, and the difference between "no opinion" and "an
   * opinion of nothing" is the whole reason this is optional.
   */
  paramSets?: SourcingPresetParamsSet[];
}

/**
 * The header the document opens with. It is not decoration: contextStack.ts injects every USER-scope
 * doc whose agent matches the brain into the Source agent's USER INSTRUCTIONS block, so this
 * document rides into every Source turn. Without this warning the agent would read a stored template
 * as a standing order and start judging shortlists the user never applied it to. It also tells the
 * user, in the doctrine workspace, exactly what the page is.
 */
const presetHeader = (lang: 'en' | 'fr'): string =>
  lang === 'fr'
    ? [
        '# Mon scoring de sourcing par defaut',
        '',
        "PRESET STOCKE, PAS UNE INSTRUCTION PERMANENTE. Ce document ne gouverne aucune shortlist tant",
        "que APPLY_SOURCING_PRESET ne l'a pas ecrit sur une shortlist ouverte. Ne t'en sers pas pour",
        'juger une liste qui ne le porte pas.',
      ].join('\n')
    : [
        '# My default sourcing scoring',
        '',
        'STORED PRESET, NOT A STANDING INSTRUCTION. This document governs no shortlist until',
        'APPLY_SOURCING_PRESET writes it onto one. Do not judge a list that does not carry it',
        'against what is written here.',
      ].join('\n');

const instructionsHeading = (lang: 'en' | 'fr'): string =>
  lang === 'fr' ? '## Instructions' : '## Instructions';

const paramsHeading = (lang: 'en' | 'fr'): string =>
  lang === 'fr' ? '## Parametres de lancement' : '## Launch parameters';

const rubricHeading = (lang: 'en' | 'fr'): string =>
  lang === 'fr' ? '## Bareme (criteres et poids)' : '## Rubric (criteria and weights)';

/** The exact field set that round-trips. Anything else a caller passes is dropped on purpose. */
const normalizeCriterion = (value: unknown): SourcingPresetCriterion | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const item = value as Record<string, unknown>;
  const id = typeof item.id === 'string' ? item.id.trim() : '';
  const label = typeof item.label === 'string' ? item.label.trim() : '';
  const weight = Number(item.weight);
  if (!id || !label || !Number.isFinite(weight)) return null;
  const description = typeof item.description === 'string' && item.description.trim().length > 0
    ? item.description.trim()
    : undefined;
  const rawMinimum = item.minimumScore;
  const minimumScore = typeof rawMinimum === 'number' && Number.isFinite(rawMinimum)
    ? rawMinimum
    : undefined;
  // Keeping the optional keys OUT of the object when unset is what makes "absent stays absent" true
  // across a save/apply cycle: an unset floor must never come back as 0. Emission order follows the
  // ScoringCriterion declaration so the stored JSON is byte-stable across saves.
  return {
    id,
    label,
    weight,
    ...(minimumScore !== undefined ? { minimumScore } : {}),
    ...(description !== undefined ? { description } : {}),
  };
};

/** Normalize a rubric coming from anywhere (API payload, stored JSON) into the preset's shape. */
/**
 * The exact parameter set that round-trips. Anything else - a key from a newer build, or one a user
 * invented while hand-editing the doctrine page - is DROPPED rather than carried, because whatever
 * comes out of here is handed to a launch payload.
 *
 * Returns undefined when nothing survives, so "no opinion" stays distinguishable from "an opinion
 * of nothing": a preset with no parameters must leave the form alone, not blank it.
 */
/** The name a set written before names existed is given, so nothing becomes unreachable. */
export const DEFAULT_PARAMS_SET_NAME = 'My parameters';

/**
 * Read the params block, in either shape. A LIST is what we write now; a bare OBJECT is what
 * documents written before names existed carry, and it is read as one set under the default name
 * rather than dropped - a user's saved setup must not vanish because the format grew.
 */
export const normalizeSourcingPresetParamSets = (value: unknown): SourcingPresetParamsSet[] | undefined => {
  if (Array.isArray(value)) {
    const sets = value
      .map((entry) => {
        const record = (entry ?? {}) as Record<string, unknown>;
        const name = typeof record.name === 'string' ? record.name.trim() : '';
        const params = normalizeSourcingPresetParams(record.params);
        return name && params ? { name, params } : null;
      })
      .filter((entry): entry is SourcingPresetParamsSet => entry !== null);
    // Last write wins on a duplicate name: saving over a name must replace it, not shadow it.
    const byName = new Map(sets.map((entry) => [entry.name, entry]));
    return byName.size > 0 ? [...byName.values()] : undefined;
  }
  const single = normalizeSourcingPresetParams(value);
  return single ? [{ name: DEFAULT_PARAMS_SET_NAME, params: single }] : undefined;
};

export const normalizeSourcingPresetParams = (value: unknown): SourcingPresetParams | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const item = value as Record<string, unknown>;
  const out: SourcingPresetParams = {};

  const num = (key: 'portfolioFitMinimumScore' | 'surfaceMinSqm' | 'surfaceMaxSqm' | 'minimumNetworkSize' | 'budgetCredits' | 'minimumFitScore') => {
    const raw = item[key];
    if (typeof raw === 'number' && Number.isFinite(raw)) out[key] = raw;
  };
  const strings = (key: 'acceptedMarketPositionings' | 'acceptedCnccSectors' | 'acceptedCnccActivities' | 'acceptedFormats' | 'countryCodes') => {
    const raw = item[key];
    if (!Array.isArray(raw)) return;
    const kept = raw.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
    // An empty array is a real answer ("no restriction"), so it is kept once the key was present.
    (out as Record<string, unknown>)[key] = kept.map((entry) => entry.trim());
  };

  num('portfolioFitMinimumScore');
  num('surfaceMinSqm');
  num('surfaceMaxSqm');
  num('minimumNetworkSize');
  num('budgetCredits');
  num('minimumFitScore');
  strings('acceptedMarketPositionings');
  strings('acceptedCnccSectors');
  strings('acceptedCnccActivities');
  strings('acceptedFormats');
  strings('countryCodes');
  (['excludeRecentlyScored', 'excludeOnsiteTenants', 'requireKnownWebsite', 'excludeOffScope'] as const)
    .forEach((key) => {
      if (typeof item[key] === 'boolean') out[key] = item[key] as boolean;
    });

  return Object.keys(out).length > 0 ? out : undefined;
};

export const normalizeSourcingPresetRubric = (value: unknown): SourcingPresetCriterion[] =>
  Array.isArray(value)
    ? value.map(normalizeCriterion).filter((item): item is SourcingPresetCriterion => item !== null)
    : [];

/**
 * Build the preset document. The rubric block goes LAST so a user hand-editing their instructions
 * never has to scroll past a wall of JSON to reach their own prose.
 */
export function serializeSourcingPreset(params: {
  instructions: string;
  rubric: unknown;
  /** Named launch-parameter sets. Omit, or pass nothing that survives, to write no block. */
  paramSets?: unknown;
  lang: 'en' | 'fr';
}): string {
  const { lang } = params;
  const instructions = typeof params.instructions === 'string' ? params.instructions.trim() : '';
  const rubric = normalizeSourcingPresetRubric(params.rubric);
  const launchParamSets = normalizeSourcingPresetParamSets(params.paramSets);

  const sections: string[] = [`${SOURCING_PRESET_DOCUMENT_MARKER}\n${presetHeader(lang)}`];
  if (instructions) {
    sections.push(
      `${instructionsHeading(lang)}\n\n${SOURCING_PRESET_INSTRUCTIONS_MARKER}\n${instructions}`,
    );
  }
  // BEFORE the rubric, always. See SOURCING_PRESET_PARAMS_MARKER.
  if (launchParamSets) {
    sections.push(
      [
        paramsHeading(lang),
        '',
        SOURCING_PRESET_PARAMS_MARKER,
        '```json',
        JSON.stringify(launchParamSets, null, 2),
        '```',
      ].join('\n'),
    );
  }
  if (rubric.length > 0) {
    sections.push(
      [
        rubricHeading(lang),
        '',
        SOURCING_PRESET_RUBRIC_MARKER,
        '```json',
        JSON.stringify(rubric, null, 2),
        '```',
      ].join('\n'),
    );
  }
  return sections.join('\n\n');
}

/**
 * Read a preset document back. Tolerant on purpose: the user can hand-edit this page in the
 * doctrine workspace, so a mangled or missing block yields an empty rubric rather than an
 * exception, and the caller decides what to tell them.
 */
export function parseSourcingPreset(content: unknown): SourcingPreset {
  const raw = typeof content === 'string' ? content : '';
  if (!raw.trim()) return { instructions: '', rubric: [], paramSets: undefined };

  // lastIndexOf, not indexOf: our block is always the last thing in the document, so a user who
  // quotes the marker inside their own prose cannot make the parser read their text as the rubric.
  const rubricAt = raw.lastIndexOf(SOURCING_PRESET_RUBRIC_MARKER);
  const rubric = rubricAt < 0 ? [] : parseRubricBlock(raw.slice(rubricAt + SOURCING_PRESET_RUBRIC_MARKER.length));

  // The params block sits between the prose and the rubric, so it is bounded ABOVE by the rubric
  // marker: without that bound a document whose params block was hand-emptied would read the
  // rubric's own fence as its parameters.
  const paramsAt = raw.indexOf(SOURCING_PRESET_PARAMS_MARKER);
  const paramsFrom = paramsAt < 0 ? -1 : paramsAt + SOURCING_PRESET_PARAMS_MARKER.length;
  const paramSets = paramsFrom < 0
    ? undefined
    : parseParamsBlock(raw.slice(paramsFrom, rubricAt > paramsFrom ? rubricAt : raw.length));

  /** Where the prose stops: whichever machine block comes first below it. */
  const firstBlockAfter = (from: number): number => {
    const candidates = [paramsAt, rubricAt].filter((at) => at > from);
    return candidates.length > 0 ? Math.min(...candidates) : -1;
  };

  // A page we did not write (the user typed it themselves in the doctrine workspace): every word of
  // it is their instruction. There is no generated header to strip because there is none.
  if (!raw.includes(SOURCING_PRESET_DOCUMENT_MARKER)) {
    const stop = firstBlockAfter(-1);
    return { instructions: cutProse(raw, stop < 0 ? raw.length : stop, stop >= 0), rubric, paramSets };
  }

  // Our own document. The instructions are exactly what sits between their marker and the rubric
  // block; no marker means the preset was saved with no prose half, which is NOT the same thing as
  // "the header is the prose".
  const instructionsAt = raw.indexOf(SOURCING_PRESET_INSTRUCTIONS_MARKER);
  if (instructionsAt < 0) return { instructions: '', rubric, paramSets };
  const from = instructionsAt + SOURCING_PRESET_INSTRUCTIONS_MARKER.length;
  const stop = firstBlockAfter(from);
  const hasBlockBelow = stop >= 0;
  return {
    instructions: cutProse(raw.slice(from), hasBlockBelow ? stop - from : raw.length, hasBlockBelow),
    rubric,
    paramSets,
  };
}

/** Read the fenced ```json block that follows the params marker. Same tolerance as the rubric. */
function parseParamsBlock(between: string): SourcingPresetParamsSet[] | undefined {
  const fence = /```json\s*\n([\s\S]*?)\n?```/.exec(between);
  if (!fence?.[1]) return undefined;
  try {
    return normalizeSourcingPresetParamSets(JSON.parse(fence[1]));
  } catch {
    return undefined;
  }
}

/** Read the fenced ```json block that follows the rubric marker. */
function parseRubricBlock(afterMarker: string): SourcingPresetCriterion[] {
  const fence = /```json\s*\n([\s\S]*?)\n?```/.exec(afterMarker);
  if (!fence?.[1]) return [];
  try {
    return normalizeSourcingPresetRubric(JSON.parse(fence[1]));
  } catch {
    // A hand-broken block is not a crash: report no rubric and let the command say so.
    return [];
  }
}

/**
 * The prose half, cut at `end`. When a rubric block follows, the last line of the slice is the
 * "## Rubric" heading that introduces it, so drop it: without that, a save/apply/save cycle stacks
 * an empty heading every round. When NOTHING follows, the last line is the user's own writing and a
 * trailing heading of theirs must survive untouched.
 */
const cutProse = (value: string, end: number, rubricFollows: boolean): string => {
  const slice = value.slice(0, end);
  return (rubricFollows ? slice.replace(/\n#{1,6}\s+[^\n]*\s*$/, '') : slice).trim();
};

/**
 * Write a preset DOWN onto one shortlist: its instructions REPLACE that shortlist's CAMPAIGN doctrine.
 * A preset's rubric half is no longer applied: every run is scored on the one grid
 * (lib/leasing/briefCriteria.ts scoringGrid), and older presets still carry a rubric block.
 */
export async function applySourcingPresetToShortlist(kernelHandle: KernelHandle, params: { campaignId: string; instructions: string }): Promise<void> {
  const { campaignId, instructions } = params;

  if (instructions.trim()) {
    const key = `campaign:${campaignId}`;
    const response = await fetch('/api/doctrine', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, content: instructions }),
    });
    if (!response.ok) {
      throw new Error(
        response.status === 403
          ? "The user's role cannot edit this shortlist's instructions."
          : `Writing the shortlist instructions failed (${response.status}).`,
      );
    }
    // A DoctrineDocument has no socket stream; this token + event are how the Source right bar and
    // an open doctrine page show the new text without a refresh (same pair UPDATE_SHORTLIST_DOCTRINE
    // uses).
    kernelHandle.setState?.({
      sourceDoctrineRefreshToken: (kernelHandle.getState?.()?.sourceDoctrineRefreshToken ?? 0) + 1,
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('doctrine:updated', { detail: { key } }));
    }
  }
}
